<script setup lang="ts">
import * as Sentry from "@sentry/browser"
import Highcharts from "highcharts"
import { useStorage } from "@vueuse/core"
import { twJoin, twMerge } from "tailwind-merge"
import { logout } from "~/plugins/auth"
import { GSKpiAggregationCat, Scope, UIFeature } from "#imports"
import { highchart_french_translations, highchart_english_translations } from "~/utils/highchart"

defineProps<{ isGlobalSearchEnabled: boolean }>()

const showGlobalSearch = defineModel<boolean>("showGlobalSearch", { required: true })

const auth0 = useAuth0()

const { hasUnseenRelease } = useReleases()

const username = computed<string>(() => auth0.user.value?.name || "local-user")
const company = computed<string>(() =>
  auth0.idTokenClaims.value
    ? auth0.idTokenClaims.value["https://renewex.co"].companyName || ""
    : "impersonate",
)

const defaultAvatarSrc = "https://api.dicebear.com/6.x/identicon/svg"
const avatarSrc = computed(() => auth0.user.value?.picture || defaultAvatarSrc)

const collapsed = useStorage(StorageKeys.UISidebarCollapsed, false)

const { t } = useI18n()

const { metaSymbol } = useShortcuts()

const { statusUrl } = useAppConfig()

// Instatus public API: `activeIncidents` / `activeMaintenances` are omitted when empty.
// Third-party call: `lazy` keeps it off the render path and every failure mode degrades
// to "no chip" rather than breaking the sidebar.
const { data: statusSummary } = useFetch<{
  activeIncidents?: unknown[]
  activeMaintenances?: unknown[]
}>(`${statusUrl}/summary.json`, {
  server: false,
  lazy: true,
  key: "instatus-summary",
  timeout: 5000,
  retry: 0,
  ignoreResponseError: true,
  default: () => null,
})

const statusChipColor = computed(() => {
  const summary = statusSummary.value
  if (R.isNullish(summary) || !R.isPlainObject(summary)) return undefined
  if (R.isArray(summary.activeIncidents) && summary.activeIncidents.length > 0) return "red"
  if (R.isArray(summary.activeMaintenances) && summary.activeMaintenances.length > 0) return "amber"
  return undefined
})

const dropdownItems = computed(() => {
  const items = [
    [
      {
        label: t("suptitle.settings"),
        icon: "i-heroicons-adjustments-horizontal",
        to: "/settings",
      },
    ],
    [{ label: t("components.actions.logout"), icon: "i-mdi-logout", click: () => logout(auth0) }],
  ]

  if (auth0.user.value) {
    if (auth0.user.value.email?.endsWith("@renewex.co")) {
      const mapCompanyNameOrgId = {
        Apex: "org_QIi2kK4hQn9A0xvY",
        "Arkolia Energies": "org_jySlLJJxvCr3OiZx",
        Barjane: "org_FUjNCilcQaQUmZGg",
        Girasole: "org_9XMDGHzPKgEPZidK",
        "Green Yellow": "org_yJssgBv43f9Th16g",
        "Green Yellow Austral": "org_cGuksnAbtgDC6lmw",
        "Green Yellow SE": "org_je8QS1dghYnsseIF",
        "GreenYellow EE": "org_KXCtOhpsedCxEtGL",
        Helexia: "org_Q7uqziQJjtV7QHrO",
        "Kallista Energy": "org_9Bin3YVLbSc5u471",
        "Nass et Wind": "org_HHyexWL3Gs004FI9",
        "R-EV": "org_0xESJAoFGubX1KaA",
        SAEM: "org_94FwcYzhcbOxQCZt",
        SeeYouSun: "org_NrkknE2PB7Qe4ubU",
        Silversun: "org_Oxv2pm92dzbhS186",
        "Smart Energies": "org_Gk1y6x3SxDTqmqRr",
        Solstyce: "org_EZtMzBZbj8FdJjGu",
        Solveo: "org_NWogT1iTq3CMmtqR",
        "Technique Solaire": "org_XcQh2O3pV7aSWTH0",
        "unit-e": "org_yVnW7Dp7f2ctDt0V",
      } as const
      items.push(
        R.pipe(
          mapCompanyNameOrgId,
          R.entries(),
          R.filter(([name, _]) => name !== company.value),
          R.map(([name, org_id]) => ({
            label: name,
            icon: "i-fa6-solid-arrow-right-arrow-left",
            click: () => logout(auth0, { switchToOrgId: org_id }),
          })),
        ),
      )
    }
  }

  return items
  //
})

const route = useRoute()
const kpiAggModePref = useStorage(
  StorageKeys.KPICompanyAggModeSideBar,
  GSKpiAggregationCat.enum.company,
)
const kpiAggregation = computed(() => route.params.aggregation ?? kpiAggModePref.value)
watch(
  () => route.params.aggregation as GSKpiAggregationCat | undefined,
  (newValue) => {
    if (!R.isNullish(newValue) && GSKpiAggregationCat.options.includes(newValue)) {
      kpiAggModePref.value = newValue
    }
  },
)

const links = computed(() => {
  const sharedOpts: Record<string, any> = { exact: false, iconClass: "text-base" }
  if (collapsed.value) {
    sharedOpts.label = undefined
  }

  return [
    { label: t("sidebar.dashboard"), to: "/dashboard", icon: "i-mdi-view-dashboard" },
    {
      label: t("sidebar.metrics"),
      to: `/metrics/${kpiAggregation.value}`,
      icon: "i-mdi-pulse",
    },
    {
      label: t("sidebar.contracts"),
      to: "/contracts",
      icon: "i-fa6-solid-file-contract",
      disabled: !hasScope(Scope.enum["read:contract"]),
    },
    { label: t("sidebar.portfolio"), to: "/portfolio", icon: "i-fa6-solid-wallet" },
    {
      label: t("sidebar.invoicing"),
      to: "/invoicing",
      icon: "i-fa6-solid-file-invoice-dollar",
      disabled: !hasScope(Scope.enum["read:invoice"]),
    },
    {
      label: t("sidebar.commissioning"),
      to: "/commissioning",
      icon: $icons.commissioning,
      disabled: !hasScope(Scope.enum["read:supervision"]),
      hidden: !hasUIFeature(UIFeature.enum.commissioning_mgmt),
    },
    {
      label: t("sidebar.directory"),
      to: "/marketplace",
      icon: "i-fa6-solid-user-group",
      iconClass: "text-sm",
      disabled: !hasScope(Scope.enum["read:partner"]),
    },
    {
      label: t("sidebar.data_studio"),
      to: "/datastudio",
      icon: $icons.database,
      disabled: !hasScope(Scope.enum["read:datastudio"]),
    },
  ].map((e) => ({ ...sharedOpts, ...e }))
})

const ui = computed(() => {
  const baseUI = /* ui */ {
    base: "group flex flex-row gap-x-2 items-center h-12 hover:bg-gray-200",
    wrapper: "mx-3 my-2 flex shrink grow flex-col gap-y-1 overflow-y-auto",
    padding: "p-4",
    font: "text-sm truncate",
    active: "text-gray-900 bg-gray-200",
    inactive: "text-gray-500 hover:text-gray-700",
    width: "w-full",
    label: "truncate relative",
    rounded: "rounded-md",
    size: "text-sm",
    icon: {
      base: "flex-shrink-0 w-4 h-4",
      active: "text-gray-700",
      inactive: "text-gray-400 group-hover:text-gray-600",
    },
  }
  if (!collapsed.value) {
    baseUI.active += " border-l-4 border-rnxblue-400"
    baseUI.icon.base += " mr-2"
  }
  return baseUI
})

const { setLocale, locale } = useI18n()

const prefLang = useStorage<string | undefined>(StorageKeys.LangPreference, undefined)
const itemsSelectLang: { lang: "fr-FR" | "en-US"; icon: string }[] = [
  { lang: "fr-FR", icon: "i-material-symbols-language-french" },
  { lang: "en-US", icon: "i-material-symbols-language-us" },
]

const defaultLang =
  itemsSelectLang.find((e) => e.lang == prefLang.value) ||
  itemsSelectLang.find((e) => e.lang == locale.value) ||
  itemsSelectLang[1]
const selectedLang = ref(defaultLang)

watch(
  selectedLang,
  (new_lang) => {
    setLocale(new_lang.lang)
    prefLang.value = new_lang.lang
    const hc_translations = {
      "fr-FR": highchart_french_translations,
      "en-US": highchart_english_translations,
    }[new_lang.lang]
    Highcharts.setOptions({ lang: hc_translations })
  },
  { immediate: true },
)

const uiBtnBottom = /* ui */ {
  font: "font-normal",
  icon: { size: { md: "size-4 mb-0.5" } },
  color: { gray: { ghost: "hover:bg-gray-200" } },
}

const feedback = Sentry.feedbackIntegration({
  colorScheme: "system",
  showBranding: false,
  autoInject: false,
})

onMounted(() => {
  feedback.attachTo(document.querySelector("#btnSentryFeedback")!, {})
})

defineShortcuts({
  meta_k: {
    usingInput: true,
    handler: () => {
      showGlobalSearch.value = !showGlobalSearch.value
    },
  },
})
</script>

<template>
  <div
    class="flex h-full shrink-0 flex-col overflow-hidden bg-gray-100 transition-width duration-200"
    :class="collapsed ? 'w-[4.5rem]' : 'w-48'"
  >
    <UDropdown
      class="h-16 w-full border-b border-gray-200"
      :items="dropdownItems"
      :popper="{ placement: 'bottom' }"
      :ui="{ container: 'z-50' }"
    >
      <UButton class="h-16 w-full" color="white" variant="ghost">
        <div class="mx-2.5 inline-flex items-center gap-x-4">
          <UAvatar size="sm" :src="avatarSrc" alt="Avatar" />

          <div v-if="!collapsed" class="w-[7.5rem] whitespace-nowrap text-left">
            <p class="block truncate font-normal">{{ username }}</p>
            <p class="block truncate font-medium">{{ company }}</p>
          </div>
        </div>
      </UButton>
    </UDropdown>

    <div class="mx-3 flex shrink-0 flex-col items-center justify-center gap-y-1 pt-1 opacity-70">
      <UButton
        id="btnAppGlobalSearch"
        size="md"
        :icon="$icons.search"
        variant="ghost"
        class="whitespace-nowrap px-4"
        :class="collapsed ? '' : 'w-full'"
        :disabled="!isGlobalSearchEnabled"
        color="gray"
        :ui="uiBtnBottom"
        @click="
          () => {
            showGlobalSearch = !showGlobalSearch
          }
        "
      >
        <tippy
          v-if="!collapsed"
          placement="right"
          theme="rnx-light"
          :arrow="false"
          :delay="[400, 0]"
        >
          <p class="ml-3">Search</p>
          <template #content>
            <div v-if="!collapsed" class="flex items-center gap-1">
              <UKbd size="xs">{{ metaSymbol }}</UKbd>
              <UKbd size="xs">K</UKbd>
            </div>
          </template>
        </tippy>
      </UButton>

      <UDivider />
    </div>

    <nav :class="ui.wrapper">
      <div v-for="(link, index) of links" :key="index">
        <component
          :is="link.disabled ? 'div' : 'ULink'"
          v-if="!link.hidden"
          v-slot="slotProps"
          v-bind="R.omit(link, ['label', 'icon'])"
          :class="[
            ui.base,
            ui.padding,
            ui.width,
            ui.rounded,
            ui.font,
            ui.size,
            link.disabled ? 'cursor-not-allowed opacity-40' : '',
          ]"
          :active-class="ui.active"
          :inactive-class="ui.inactive"
          @keyup.enter="$event.target.blur()"
        >
          <UIcon
            v-if="link.icon"
            :name="link.icon"
            :class="
              twMerge(
                twJoin(ui.icon.base, slotProps?.isActive ? ui.icon.active : ui.icon.inactive),
                link.iconClass,
              )
            "
          />

          <span v-if="link.label" :class="twMerge(ui.label, (link as any).labelClass)">
            {{ link.label }}
          </span>
        </component>
      </div>
    </nav>

    <div class="mx-3 flex shrink-0 flex-col items-center justify-center gap-y-0">
      <UDivider v-if="!collapsed">
        <NuxtImg provider="imagekit" src="/zefire-spa/sigle_renewex.svg" class="my-2 max-w-8" />
      </UDivider>
      <UDivider v-else class="mb-5" />

      <UButton
        id="btnSentryFeedback"
        size="md"
        icon="i-heroicons-megaphone"
        variant="ghost"
        :label="collapsed ? '' : $t('sidebar.feedback')"
        class="whitespace-nowrap p-4"
        :class="collapsed ? '' : 'w-full'"
        color="gray"
        :ui="uiBtnBottom"
      />

      <!-- release notes link -->
      <UButton
        size="md"
        variant="ghost"
        :active-class="
          !collapsed
            ? 'border-rnxblue-400 border-l-4 text-gray-900 bg-gray-200'
            : 'text-gray-900 bg-gray-200'
        "
        :label="collapsed ? '' : t('sidebar.releases')"
        class="p-4"
        :class="collapsed ? '' : 'w-full'"
        to="/releases"
        color="gray"
        :ui="uiBtnBottom"
      >
        <template #leading>
          <UChip :show="hasUnseenRelease" color="red" size="sm" :inset="false">
            <UIcon name="i-heroicons-sparkles" class="mb-0.5 size-4" />
          </UChip>
        </template>
      </UButton>

      <!-- doc link -->
      <UButton
        icon="i-heroicons-book-open"
        size="md"
        variant="ghost"
        :active-class="
          !collapsed
            ? 'border-rnxblue-400 border-l-4 text-gray-900 bg-gray-200'
            : 'text-gray-900 bg-gray-200'
        "
        :label="collapsed ? '' : 'Documentation'"
        class="p-4"
        :class="collapsed ? '' : 'w-full'"
        :to="useRuntimeConfig().public.notionUrl"
        target="_blank"
        external
        color="gray"
        :ui="uiBtnBottom"
      />

      <!-- status page link -->
      <UButton
        size="md"
        variant="ghost"
        :label="collapsed ? '' : t('sidebar.status')"
        class="p-4"
        :class="collapsed ? '' : 'w-full'"
        :to="statusUrl"
        target="_blank"
        external
        color="gray"
        :ui="uiBtnBottom"
      >
        <template #leading>
          <UChip
            :show="!R.isNullish(statusChipColor)"
            :color="statusChipColor"
            size="sm"
            :inset="false"
          >
            <UIcon name="i-heroicons-signal" class="mb-0.5 size-4" />
          </UChip>
        </template>
      </UButton>

      <USelectMenu
        v-model="selectedLang"
        size="md"
        color="gray"
        variant="none"
        :trailing-icon="collapsed ? '' : 'i-heroicons-chevron-up-20-solid'"
        :options="itemsSelectLang"
        :ui="{
          wrapper: collapsed ? 'pb-4 pt-2' : 'w-full ml-4 pb-4 pt-2',
          base:
            'relative focus:outline-none overflow-y-auto scroll-py-1' + collapsed ? '' : ' w-full',
        }"
        :ui-menu="{
          width: 'w-40',
        }"
      >
        <template #label>
          <div class="inline-flex items-center">
            <UIcon class="mb-0.5 size-5 text-gray-500" color="gray" :name="selectedLang.icon" />
            <p v-if="!collapsed" class="pl-2 text-gray-500">
              {{ t(selectedLang.lang) }}
            </p>
          </div>
        </template>
        <template #option="{ option }">
          <div class="inline-flex items-center gap-x-2">
            <UIcon class="mb-0.5 size-5 text-gray-500" color="gray" :name="option.icon" />
            <p v-if="!collapsed" class="text-gray-500">
              {{ t(option.lang) }}
            </p>
          </div>
        </template>
      </USelectMenu>
    </div>
  </div>
  <div class="relative">
    <UButton
      class="absolute -right-3 top-[3.1rem] z-10 shadow-md"
      size="xs"
      color="white"
      variant="solid"
      :ui="{ rounded: 'rounded-full', font: 'font-normal', icon: { size: { xs: 'text-sm' } } }"
      :icon="collapsed ? 'i-ri-arrow-right-s-line' : 'i-ri-arrow-left-s-line'"
      @click="collapsed = !collapsed"
    />
  </div>
  <NotificationCenter />
</template>

<style>
#sentry-feedback {
  --inset: auto auto 0 0;
}
</style>
